import {
    Component,
    input,
    output,
    signal,
    computed,
    ChangeDetectionStrategy,
    effect
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { Step } from '../interfaces/Step.interface';
import { SoBadgeUI, SoBadgeColor } from '../so-badge/so-badge.component';

export type SoStepperOrientation = 'horizontal' | 'vertical';
export type SoStepperVariant = 'default' | 'pill';

@Component({
    selector: 'so-stepper-ui',
    standalone: true,
    imports: [CommonModule, MatTooltipModule, MatIconModule, SoBadgeUI],
    templateUrl: './so-stepper-ui.component.html',
    styleUrl: './so-stepper-ui.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SoStepperUI {
    // Inputs using signals
    steps = input.required<Step[]>();
    activeStep = input<number>(0);
    allowStepsToBeNavigated = input<number[]>([]);
    orientation = input<SoStepperOrientation>('horizontal');
    compact = input<boolean>(false);
    variant = input<SoStepperVariant>('default');

    // Output
    activeStepChange = output<number>();
    stepNavigate = output<{ route: string; index: number }>();

    // Internal state
    protected internalActiveStep = signal<number>(0);

    // Computed values
    protected isHorizontal = computed(() => this.orientation() === 'horizontal');
    protected isVertical = computed(() => this.orientation() === 'vertical');
    protected isPill = computed(() => this.variant() === 'pill');

    constructor() {
        // Sync activeStep input with internal state
        effect(() => {
            const active = this.activeStep();
            this.internalActiveStep.set(active);
        }, {  });
    }

    getLastActiveIndex(): number {
        const steps = this.steps();
        return steps.findIndex((s: Step) => s.status === 'active') + 1;
    }

    navigateToStep(route: string, nextIndex: number): void {
        if (this.isStepNavigationDisabled(nextIndex)) return;
        this.updateStepStatus(nextIndex);
        this.stepNavigate.emit({ route, index: nextIndex });
    }

    updateStepStatus(nextIndex: number): void {
        const currentActive = this.internalActiveStep();
        if (currentActive === nextIndex) return;

        const steps = this.steps();
        steps.forEach((step, index) => {
            if (index < nextIndex) {
                step.status = 'completed';
            } else if (index === nextIndex) {
                step.status = 'active';
            } else {
                step.status = 'pending';
            }
        });

        this.internalActiveStep.set(nextIndex);
        this.activeStepChange.emit(nextIndex);
    }

    protected stepTooltip(item: Step, stepIndex: number): string {
        if (this.isStepNavigationDisabled(stepIndex)) {
            return item.disabledTooltip ?? (this.isPill() ? item.text : '');
        }
        return this.isPill() ? item.text : '';
    }

    isStepNavigationDisabled(stepIndex: number): boolean {
        const allowed = this.allowStepsToBeNavigated();
        return !allowed.includes(stepIndex);
    }

    getBadgeText(step: Step): string {
        if (step.badgeText) return step.badgeText;
        switch (step.status) {
            case 'completed': return 'Completado';
            case 'active': return 'En Progreso';
            case 'pending': return 'Pendiente';
            default: return '';
        }
    }

    getBadgeColor(step: Step): SoBadgeColor {
        if (step.badgeColor) return step.badgeColor as SoBadgeColor;
        switch (step.status) {
            case 'completed': return 'secondary';
            case 'active': return 'primary-light';
            case 'pending': return 'grey';
            default: return 'grey';
        }
    }
}
